import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Message } from '@shared/types';
import { useConversation } from '@/services/conversationService';
import { useCurrentMessage } from '@/contexts/CurrentMessageContext';
import { useMessagesQuery } from '@/services/messageService';
import Tree from '@shared/Tree';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { ChatSection } from '@/components/chat/ChatSection';
import { Button } from '@/components/ui/button';
import { MessageSquare, Settings2, PanelRightClose, X, Download } from 'lucide-react';
import { ViewerSection } from '@/components/viewer/ViewerSection';
import { ParameterSection } from '@/components/parameter/ParameterSection';
import { ExportSection } from '@/components/ExportSection';
import { useBlob } from '@/contexts/BlobContext';
import { useColor } from '@/contexts/ColorContext';
import { cn } from '@/lib/utils';
import { useOutletContext } from 'react-router-dom';

const PANEL_SIZES = {
  RIGHT_PANEL: {
    DEFAULT: 35,
    MIN: 320,
    MAX: 500,
  },
  VIEWER: {
    DEFAULT: 65,
    MIN: 40,
  },
} as const;

// Tab types for the right panel
type RightPanelTab = 'chat' | 'parameters' | 'export';

export function ParametricEditor() {
  const { conversation } = useConversation();
  const { currentMessage, setCurrentMessage } = useCurrentMessage();
  const { setBlob } = useBlob();
  const { setColor } = useColor();
  const { isMobile } = useOutletContext<{ isMobile?: boolean }>();

  // State for the right panel
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<RightPanelTab>('chat');
  const rightPanelRef = useRef<ImperativePanelHandle>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Mobile-specific state
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);

  const { data: messages = [] } = useMessagesQuery();

  const lastMessage = useMemo(() => {
    if (conversation.current_message_leaf_id) {
      return messages.find(
        (msg) => msg.id === conversation.current_message_leaf_id,
      );
    }
    return messages[messages.length - 1];
  }, [messages, conversation.current_message_leaf_id]);

  const messageTree = useMemo(() => {
    return new Tree<Message>(messages);
  }, [messages]);

  const currentMessageBranch = useMemo(() => {
    return messageTree.getPath(lastMessage?.id ?? '');
  }, [lastMessage, messageTree]);

  useEffect(() => {
    currentMessageBranch.forEach((message) => {
      if (message.id === currentMessage?.id) {
        setCurrentMessage(message);
      }
    });
  }, [currentMessageBranch, currentMessage, setCurrentMessage]);

  useEffect(() => {
    setCurrentMessage(null);
    setBlob(null);
    setColor('#00A6FF');
  }, [conversation.id, setCurrentMessage, setBlob, setColor]);

  useEffect(() => {
    if (lastMessage?.role === 'assistant') {
      setCurrentMessage(lastMessage);
    }
  }, [lastMessage, setCurrentMessage]);

  const setContainerRef = useCallback((element: HTMLDivElement) => {
    setContainerWidth(element.offsetWidth);

    resizeObserverRef.current = new ResizeObserver(() => {
      setContainerWidth(element.offsetWidth);
    });
    resizeObserverRef.current.observe(element);
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, []);

  const rightPanelSizes = useMemo(() => {
    if (containerWidth === 0)
      return { defaultSize: 35, minSize: 0, maxSize: 100 };

    const minSize = (PANEL_SIZES.RIGHT_PANEL.MIN / containerWidth) * 100;
    const maxSize = (PANEL_SIZES.RIGHT_PANEL.MAX / containerWidth) * 100;
    const defaultSize = Math.min(
      Math.max(PANEL_SIZES.RIGHT_PANEL.DEFAULT, minSize),
      maxSize,
    );
    return {
      defaultSize,
      minSize,
      maxSize,
    };
  }, [containerWidth]);

  const hasArtifact = useMemo(
    () => !!currentMessage?.content.artifact,
    [currentMessage],
  );

  const handleRightPanelCollapse = useCallback(() => {
    const panel = rightPanelRef.current;
    if (panel) {
      panel.collapse();
      setIsRightPanelCollapsed(true);
    }
  }, []);

  const handleRightPanelExpand = useCallback(() => {
    const panel = rightPanelRef.current;
    if (panel) {
      panel.expand();
      setIsRightPanelCollapsed(false);
    }
  }, []);

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="relative flex h-full w-full flex-col overflow-hidden bg-dc-bg-primary">
        {/* 3D Viewer - Full Screen */}
        <div className="flex-1 overflow-hidden">
          <ViewerSection />
        </div>

        {/* Mobile Tab Bar */}
        <div className="flex h-14 shrink-0 items-center justify-around border-t border-dc-border-secondary bg-dc-bg-secondary/80 backdrop-blur-sm">
          <button
            onClick={() => {
              setActiveTab('chat');
              setIsMobileChatOpen(true);
            }}
            className={cn(
              'flex flex-col items-center gap-0.5 px-4 py-2 transition-colors',
              'text-dc-text-secondary hover:text-dc-accent-primary'
            )}
          >
            <MessageSquare className="h-5 w-5" />
            <span className="text-xs font-medium">Chat</span>
          </button>

          {hasArtifact && (
            <button
              onClick={() => {
                setActiveTab('parameters');
                setIsMobileChatOpen(true);
              }}
              className={cn(
                'flex flex-col items-center gap-0.5 px-4 py-2 transition-colors',
                'text-dc-text-secondary hover:text-dc-accent-secondary'
              )}
            >
              <Settings2 className="h-5 w-5" />
              <span className="text-xs font-medium">Params</span>
            </button>
          )}

          {hasArtifact && (
            <button
              onClick={() => {
                setActiveTab('export');
                setIsMobileChatOpen(true);
              }}
              className={cn(
                'flex flex-col items-center gap-0.5 px-4 py-2 transition-colors',
                'text-dc-text-secondary hover:text-dc-accent-tertiary'
              )}
            >
              <Download className="h-5 w-5" />
              <span className="text-xs font-medium">Export</span>
            </button>
          )}
        </div>

        {/* Mobile Chat/Parameters Overlay */}
        {isMobileChatOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsMobileChatOpen(false)}
            />
            {/* Panel */}
            <div className="fixed inset-x-0 bottom-0 z-50 h-[70vh] animate-in slide-in-from-bottom duration-300">
              <div className="flex h-full flex-col rounded-t-2xl border-t border-dc-border-secondary bg-dc-bg-primary">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-dc-border-secondary px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setActiveTab('chat')}
                      className={cn(
                        'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-all',
                        activeTab === 'chat'
                          ? 'bg-dc-accent-primary/20 text-dc-accent-primary'
                          : 'text-dc-text-tertiary'
                      )}
                    >
                      <MessageSquare className="h-4 w-4" />
                      Chat
                    </button>
                    {hasArtifact && (
                      <button
                        onClick={() => setActiveTab('parameters')}
                        className={cn(
                          'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-all',
                          activeTab === 'parameters'
                            ? 'bg-dc-accent-secondary/20 text-dc-accent-secondary'
                            : 'text-dc-text-tertiary'
                        )}
                      >
                        <Settings2 className="h-4 w-4" />
                        Params
                      </button>
                    )}
                    {hasArtifact && (
                      <button
                        onClick={() => setActiveTab('export')}
                        className={cn(
                          'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-all',
                          activeTab === 'export'
                            ? 'bg-gradient-to-r from-dc-accent-primary/20 to-dc-accent-secondary/20 text-dc-accent-primary'
                            : 'text-dc-text-tertiary'
                        )}
                      >
                        <Download className="h-4 w-4" />
                        Export
                      </button>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-dc-text-tertiary"
                    onClick={() => setIsMobileChatOpen(false)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>

                {/* Content */}
                <div
                  className={activeTab === 'chat' ? 'flex min-h-0 flex-1 flex-col' : 'min-h-0 flex-1 overflow-auto'}
                >
                  {activeTab === 'chat' && (
                    <ChatSection messages={currentMessageBranch ?? []} />
                  )}
                  {activeTab === 'parameters' && (
                    <ParameterSection />
                  )}
                  {activeTab === 'export' && (
                    <ExportSection />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Desktop Layout
  return (
    <div
      className="relative flex h-full w-full overflow-hidden bg-dc-bg-primary"
      ref={setContainerRef}
    >
      {/* Floating button to show right panel when collapsed */}
      {isRightPanelCollapsed && (
        <Button
          onClick={handleRightPanelExpand}
          className={cn(
            'absolute right-4 top-4 z-50 flex items-center gap-2 rounded-xl px-4 py-2',
            'bg-gradient-to-r from-dc-accent-primary/20 to-dc-accent-secondary/20',
            'border border-dc-accent-primary/40 text-dc-text-primary',
            'shadow-[0_0_20px_rgba(0,212,255,0.2)] backdrop-blur-xl',
            'transition-all duration-300',
            'hover:border-dc-accent-primary hover:shadow-[0_0_30px_rgba(0,212,255,0.3)]',
          )}
        >
          <MessageSquare className="h-4 w-4 text-dc-accent-primary" />
          <span className="text-sm font-medium">Show Panel</span>
        </Button>
      )}

      <PanelGroup
        direction="horizontal"
        className="h-full w-full"
      >
        {/* Viewer Panel - LEFT SIDE */}
        <Panel
          defaultSize={PANEL_SIZES.VIEWER.DEFAULT}
          minSize={PANEL_SIZES.VIEWER.MIN}
          id="viewer-panel"
          order={0}
        >
          <ViewerSection />
        </Panel>

        <PanelResizeHandle className="resize-handle group relative" />

        {/* Right Panel - Chat + Parameters with tabs */}
        <Panel
          collapsible
          ref={rightPanelRef}
          defaultSize={rightPanelSizes.defaultSize}
          minSize={rightPanelSizes.minSize}
          maxSize={rightPanelSizes.maxSize}
          id="right-panel"
          order={1}
          onCollapse={() => setIsRightPanelCollapsed(true)}
          onExpand={() => setIsRightPanelCollapsed(false)}
        >
          <div className="flex h-full flex-col border-l border-dc-border-secondary bg-dc-bg-primary">
            {/* Tab Header */}
            <div className="flex items-center justify-between border-b border-dc-border-secondary bg-dc-bg-secondary/50 px-2 py-2">
              <div className="flex items-center gap-1">
                {/* Chat Tab */}
                <button
                  onClick={() => setActiveTab('chat')}
                  className={cn(
                    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all',
                    activeTab === 'chat'
                      ? 'bg-dc-accent-primary/20 text-dc-accent-primary'
                      : 'text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary'
                  )}
                >
                  <MessageSquare className="h-4 w-4" />
                  Chat
                </button>

                {/* Parameters Tab - only shown when there's an artifact */}
                {hasArtifact && (
                  <button
                    onClick={() => setActiveTab('parameters')}
                    className={cn(
                      'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all',
                      activeTab === 'parameters'
                        ? 'bg-dc-accent-secondary/20 text-dc-accent-secondary'
                        : 'text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary'
                    )}
                  >
                    <Settings2 className="h-4 w-4" />
                    Parameters
                  </button>
                )}

                {/* Export Tab - only shown when there's an artifact */}
                {hasArtifact && (
                  <button
                    onClick={() => setActiveTab('export')}
                    className={cn(
                      'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all',
                      activeTab === 'export'
                        ? 'bg-gradient-to-r from-dc-accent-primary/20 to-dc-accent-secondary/20 text-dc-accent-primary'
                        : 'text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary'
                    )}
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </button>
                )}
              </div>

              {/* Collapse button */}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary"
                onClick={handleRightPanelCollapse}
              >
                <PanelRightClose className="h-4 w-4" />
              </Button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-hidden">
              {activeTab === 'chat' && (
                <ChatSection messages={currentMessageBranch ?? []} />
              )}
              {activeTab === 'parameters' && (
                <ParameterSection />
              )}
              {activeTab === 'export' && (
                <ExportSection />
              )}
            </div>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
}

